import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  clearApplication,
  setApplicationError,
  setApplicationLoading,
} from '../../store/slices/apply/applySlice';
import {
  selectApplicationError,
  selectApplicationFinished,
  selectApplicationIsPaymentRequired,
  selectApplicationLoading,
  selectPermitApplicationData,
} from '../../store/slices/apply/applySelectors';
import {
  selectConfigExpressApplyRentalStructureRequired,
  selectConfigExpressLocalContactVerbiage,
  selectConfigExpressRegistrationTypeVerbiage,
  selectConfigExpressParcelVerbiage,
  selectConfigExpressApply,
} from '../../store/slices/config/configSelectors';
import { confirmApplication } from '../../store/slices/apply/applyThunks';
import usePlaceNavigate from '../../hooks/usePlaceNavigate';
import { hasAllRequiredDocuments } from 'src/common/documents';
import {
  payNothing,
  PayNothingOutcome,
} from '../../store/slices/payment/paymentThunk';
import { selectCurrentUserEmail } from '../../store/slices/auth/authSelectors';
import { FLOWS_ENUM } from '../../common/enums';
import { ConfirmationPage } from 'src/components/pages/ConfirmationPage';
import { RegistrationFlow } from 'src/types/configuration';
import { useAppDispatch } from 'src/store/store';
import { ApplicationData } from 'src/types/flow';

const ERROR_MESSAGE =
  'Unable to successfully finalize the new license creation.';
const CONFIRM_PAYMENT =
  'Please confirm the information below to proceed to the payment step.';
const CONFIRM_WITHOUT_PAYMENT =
  'Please confirm the following information and sign below.';

const ApplyConfirmation = () => {
  const placeNavigate = usePlaceNavigate();
  const dispatch = useAppDispatch();
  const configRenew = useSelector(selectConfigExpressApply);
  const [errorMessage, setErrorMessage] = useState('');

  /** Current Flow State */
  const applicationLoading = useSelector(selectApplicationLoading);
  const applicationFinished = useSelector(selectApplicationFinished);
  const applicationError = useSelector(selectApplicationError);
  const selectedPermitApplicationData = useSelector(
    selectPermitApplicationData
  );
  const selectedEmailAddress = useSelector(selectCurrentUserEmail);
  const selectedPaymentRequired = useSelector(
    selectApplicationIsPaymentRequired
  );

  /* Configurattion Related Variables */
  const selectedRentalStructureRequiredStatus = useSelector(
    selectConfigExpressApplyRentalStructureRequired
  );
  const localContactVerbiage = useSelector(
    selectConfigExpressLocalContactVerbiage
  );
  const permitTypeVerbiage = useSelector(
    selectConfigExpressRegistrationTypeVerbiage
  );
  const parcelVerbiage = useSelector(selectConfigExpressParcelVerbiage);

  useEffect(() => {
    if (applicationError) {
      setErrorMessage(applicationError);
    }
  }, [applicationError]);

  const onPayNothingCompleted = useCallback(
    (result?: PayNothingOutcome) => {
      if (result?.success) {
        placeNavigate('/apply-success');
        dispatch(setApplicationLoading(false));
      } else {
        const message = result?.errorMessage ?? ERROR_MESSAGE;
        setErrorMessage(message);
        dispatch(setApplicationError(message));
      }
    },
    [placeNavigate, dispatch]
  );

  useEffect(() => {
    if (applicationFinished) {
      if (selectedPaymentRequired) {
        placeNavigate('/apply-payment');
        dispatch(setApplicationLoading(false));
      } else {
        dispatch(payNothing(selectedEmailAddress ?? '', onPayNothingCompleted));
      }
    }
  }, [
    applicationFinished,
    dispatch,
    onPayNothingCompleted,
    placeNavigate,
    selectedEmailAddress,
    selectedPaymentRequired,
  ]);

  useEffect(() => {
    if (
      selectedPermitApplicationData &&
      !hasAllRequiredDocuments(
        selectedPermitApplicationData.documents,
        configRenew?.documents
      )
    ) {
      placeNavigate('/apply-documents');
    }
  }, [configRenew, placeNavigate, selectedPermitApplicationData]);

  const onGoBack = useCallback(() => {
    dispatch(clearApplication());
  }, [dispatch]);

  const onConfirmation = useCallback(
    (data: ApplicationData) => {
      if (!applicationLoading) {
        dispatch(confirmApplication(data));
      }
    },
    [applicationLoading, dispatch]
  );

  const instructionsText = useMemo(
    () => (selectedPaymentRequired ? CONFIRM_PAYMENT : CONFIRM_WITHOUT_PAYMENT),
    [selectedPaymentRequired]
  );

  if (!selectedPermitApplicationData) return null;

  return (
    <ConfirmationPage
      config={configRenew as RegistrationFlow}
      onGoBack={onGoBack}
      data={selectedPermitApplicationData}
      documentUploadErrors={[applicationError]}
      flow={FLOWS_ENUM.APPLY_LICENSE}
      flowError={errorMessage}
      instructionsText={instructionsText}
      loading={applicationLoading}
      localContactVerbiage={localContactVerbiage}
      onConfirmation={onConfirmation}
      parcelVerbiage={parcelVerbiage}
      permitTypeVerbiage={permitTypeVerbiage}
      rentalStructureRequiredStatus={selectedRentalStructureRequiredStatus}
    />
  );
};

export default ApplyConfirmation;
